import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
} from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import _isNil from 'lodash/isNil';
import colors from '../../../../styles/colors';
import { JobPosition, JobType } from '../PositionType';
import { getTranslatedValue } from '../../../../utils/localizedString';
import { useStoreState } from '../../../../models/hooks';
import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import { AdditionalNoteEditor } from '../../../../app/components/AdditionalNoteEditor';
import { LanguageMap } from '../../../../firebase/firestore/documents/user';

export type AdvanceFieldsProps = {
  jobPositionData: JobPosition;
  setJobPositionData: React.Dispatch<React.SetStateAction<JobPosition>>;
  jobList: JobType[];
  handleSkillChange: (value: number, id: number) => void;
  isEnglishOfferEmpty?: boolean;
  setIsEnglishOfferEmpty?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const AdvanceFieldsCard = ({
  jobPositionData,
  setJobPositionData,
  jobList,
  handleSkillChange,
  isEnglishOfferEmpty,
  setIsEnglishOfferEmpty,
}: AdvanceFieldsProps): JSX.Element => {
  const { t, i18n } = useTranslation();

  const isOfferConditionsEnabled = useStoreState((s) => s.app.accounts?.configuration?.offerConditionsEnabled);

  const enableNewUiForCreatePosition: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_NEW_UI_FOR_CREATE_POSITION];
  });

  const listSkills = () => {
    // eslint-disable-next-line radix
    const filterJob = jobList?.filter((job) => job.id === parseInt(jobPositionData.job))[0];
    return (
      filterJob.skills.length > 0 &&
      filterJob.skills.map((skill, index) => (
        <Box w="50%" pr={index % 2 === 0 ? 8 : 0} key={skill.id} mb={4}>
          <FormControl>
            <FormLabel htmlFor={`skill-${skill.id}`}>{getTranslatedValue(skill.name, i18n.language)}</FormLabel>
            <Slider
              value={
                jobPositionData?.skills?.length
                  ? jobPositionData?.skills?.filter((skl) => skl?.id === skill?.id)?.[0]?.value
                  : 0.5
              }
              min={0}
              max={1}
              step={0.1}
              data-testid={`SkillSlider-${skill.id}`}
              id={`skill-${skill.id}`}
              onChange={/* istanbul ignore next */ (value: number) => handleSkillChange(value, skill.id)}
            >
              <SliderTrack bg="rgba(48, 127, 226, 0.3)" h="8px" borderRadius="3px">
                <Box position="relative" right={10} />
                <SliderFilledTrack bg="blue.500" />
              </SliderTrack>
              <SliderThumb bg="blue.500" boxSize={6} />
            </Slider>
          </FormControl>
        </Box>
      ))
    );
  };

  // istanbul ignore next
  const handleAdditionalNote = (value: string | LanguageMap<string> | undefined) => {
    setIsEnglishOfferEmpty?.(false);
    if (!_isNil(setJobPositionData)) {
      setJobPositionData((prev) => ({
        ...prev,
        offerConditions: value,
      }));
    }
  };

  return (
    <Box backgroundColor={colors.white} borderRadius={5} mb={6}>
      <Accordion borderWidth={0} defaultIndex={[0]} allowToggle>
        <AccordionItem borderWidth={0} borderColor="#fff" isDisabled={!jobPositionData.job}>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight={700} fontSize="16px">
                {t('createJobPosition:advanceAccordionLabel')}
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel p={0} borderWidth={0}>
            <Divider borderColor={colors.gray[50]} />
            <Box px={4}>
              <FormControl isInvalid={isEnglishOfferEmpty} isRequired>
                {isOfferConditionsEnabled && (
                  <AdditionalNoteEditor
                    additionalNote={jobPositionData?.offerConditions}
                    handleNote={handleAdditionalNote}
                    showToolbar
                    showHeader
                  />
                )}
                <FormErrorMessage>{t('jobPosition:offerConditionRequired')}</FormErrorMessage>
              </FormControl>
              <Heading as="h3" fontSize="15px" lineHeight="1.3" mb={1} mt={2}>
                {t('createJobPosition:skillsCardHeader')}
              </Heading>
              <Text fontSize="xs" color="gray.500">
                {t('createJobPosition:skillsCardHint')}
                <span style={{ color: colors.blue['500'], marginLeft: '5px' }}>
                  {t('createJobPosition:skillsCardHintSlideText')}
                </span>
              </Text>
            </Box>
            <Divider borderColor={colors.gray[50]} />
            <Box p={/* istanbul ignore next */ enableNewUiForCreatePosition ? 3 : 6}>
              <Flex flexWrap="wrap">{jobList && jobPositionData?.job && listSkills()}</Flex>
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};
